import { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import type { SaveSourceTextToSkipBody, SourceTextToSkip } from "@rebuild/shared";
import {
	deleteSourceTextToSkip,
	fetchSourceTextsToSkip,
	saveSourceTextToSkip,
} from "./processorConfigAPI";

type DraftEntry = SaveSourceTextToSkipBody;

function SourceTextsToSkipComponent() {
	const [entries, setEntries] = useState<SourceTextToSkip[]>([]);
	const [isLoading, setIsLoading] = useState(false);
	const [draft, setDraft] = useState<DraftEntry | undefined>();
	const [entryToDelete, setEntryToDelete] = useState<SourceTextToSkip | undefined>();
	const [isSaving, setIsSaving] = useState(false);

	const refresh = useCallback(async () => {
		setIsLoading(true);
		try {
			setEntries(await fetchSourceTextsToSkip());
		} catch (e) {
			toast.error("Failed to load source texts to skip");
		} finally {
			setIsLoading(false);
		}
	}, []);

	useEffect(() => {
		refresh();
	}, [refresh]);

	const onSave = async () => {
		if (!draft) return;
		if (!draft.exampleText?.trim()) {
			toast.error("Example text is required");
			return;
		}
		setIsSaving(true);
		try {
			await saveSourceTextToSkip(draft);
			await refresh();
			setDraft(undefined);
		} catch (e) {
			toast.error("Failed to save entry");
		} finally {
			setIsSaving(false);
		}
	};

	const onDelete = async () => {
		if (!entryToDelete) return;
		try {
			await deleteSourceTextToSkip(entryToDelete.id);
			await refresh();
		} catch (e) {
			toast.error("Failed to delete entry");
		} finally {
			setEntryToDelete(undefined);
		}
	};

	const isEditing = !!draft?.id;

	return (
		<>
			<div className="bg-white p-6 rounded-lg shadow">
				<div className="flex justify-between items-center mb-2">
					<h2 className="text-xl font-semibold text-gray-800">Source Texts To Skip</h2>
					<button
						onClick={() => setDraft({ exampleText: "", reasoning: "" })}
						className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
					>
						Add Entry
					</button>
				</div>
				<p className="text-sm text-gray-500 mb-4">
					Source text examples that the template-selection agent should never generate line items for (e.g. "bagging of debris").
				</p>

				{isLoading && entries.length === 0 ? (
					<p className="text-gray-500">Loading...</p>
				) : entries.length === 0 ? (
					<div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
						<p className="text-gray-500">No entries added yet.</p>
					</div>
				) : (
					<table className="w-full text-left border-collapse">
						<thead>
							<tr className="border-b border-gray-200">
								<th className="py-2 px-3 text-sm font-semibold text-gray-700">Example Text</th>
								<th className="py-2 px-3 text-sm font-semibold text-gray-700">Reasoning</th>
								<th className="py-2 px-3 text-sm font-semibold text-gray-700 w-32">Actions</th>
							</tr>
						</thead>
						<tbody>
							{entries.map((entry) => (
								<tr key={entry.id} className="border-b border-gray-100 hover:bg-gray-50">
									<td className="py-2 px-3 text-gray-800">{entry.exampleText}</td>
									<td className="py-2 px-3 text-gray-600">{entry.reasoning ?? "—"}</td>
									<td className="py-2 px-3">
										<div className="flex gap-2">
											<button
												onClick={() => setDraft({ id: entry.id, exampleText: entry.exampleText, reasoning: entry.reasoning })}
												className="px-3 py-1 text-sm text-blue-600 hover:bg-blue-50 rounded-md"
											>
												Edit
											</button>
											<button
												onClick={() => setEntryToDelete(entry)}
												className="px-3 py-1 text-sm bg-red-600 hover:bg-red-700 text-white rounded-md"
											>
												Delete
											</button>
										</div>
									</td>
								</tr>
							))}
						</tbody>
					</table>
				)}
			</div>

			{draft && (
				<EditModal
					title={isEditing ? "Edit Entry" : "Add Entry"}
					draft={draft}
					onChange={setDraft}
					onClose={() => setDraft(undefined)}
					onSave={onSave}
					isSaving={isSaving}
				/>
			)}

			{entryToDelete && (
				<ConfirmDeleteModal
					entry={entryToDelete}
					onClose={() => setEntryToDelete(undefined)}
					onConfirm={onDelete}
				/>
			)}
		</>
	);
}

interface EditModalProps {
	title: string;
	draft: DraftEntry;
	onChange: (draft: DraftEntry) => void;
	onClose: () => void;
	onSave: () => void;
	isSaving: boolean;
}

function EditModal({ title, draft, onChange, onClose, onSave, isSaving }: EditModalProps) {
	return (
		<div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
			<div className="bg-white rounded-lg shadow-lg w-[600px] max-w-[90vw] relative">
				<button onClick={onClose} className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 text-2xl">
					×
				</button>
				<div className="p-6">
					<h2 className="text-xl font-bold text-gray-800 mb-6">{title}</h2>
					<div className="space-y-4">
						<div>
							<label className="block text-sm font-medium text-gray-700 mb-1">Example Text</label>
							<textarea
								className="w-full border border-gray-300 rounded-md p-2 text-sm"
								rows={3}
								value={draft.exampleText}
								onChange={(e) => onChange({ ...draft, exampleText: e.target.value })}
								placeholder='e.g. "bagging of debris"'
							/>
						</div>
						<div>
							<label className="block text-sm font-medium text-gray-700 mb-1">Reasoning (optional)</label>
							<textarea
								className="w-full border border-gray-300 rounded-md p-2 text-sm"
								rows={3}
								value={draft.reasoning ?? ""}
								onChange={(e) => onChange({ ...draft, reasoning: e.target.value })}
								placeholder="Why should the agent skip this?"
							/>
						</div>
					</div>
					<button
						onClick={onSave}
						disabled={isSaving}
						className="mt-6 w-full bg-blue-500 hover:bg-blue-600 disabled:bg-gray-400 disabled:cursor-not-allowed text-white font-medium py-2 px-4 rounded-lg transition-colors"
					>
						{isSaving ? "Saving..." : "Save"}
					</button>
				</div>
			</div>
		</div>
	);
}

interface ConfirmDeleteModalProps {
	entry: SourceTextToSkip;
	onClose: () => void;
	onConfirm: () => void;
}

function ConfirmDeleteModal({ entry, onClose, onConfirm }: ConfirmDeleteModalProps) {
	return (
		<div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
			<div className="bg-white rounded-lg shadow-lg w-96 relative">
				<div className="p-6">
					<h2 className="text-xl font-bold text-gray-800 mb-4">Delete Entry</h2>
					<p className="text-gray-600 mb-6">
						Delete "{entry.exampleText}"? This action cannot be undone.
					</p>
					<div className="flex gap-3">
						<button
							onClick={onClose}
							className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium"
						>
							Cancel
						</button>
						<button
							onClick={onConfirm}
							className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium"
						>
							Delete
						</button>
					</div>
				</div>
			</div>
		</div>
	);
}

export default SourceTextsToSkipComponent;
